import React from 'react';
import PropTypes from 'prop-types';
import { Button, Icon } from 'semantic-ui-react';
import { withTranslation } from 'react-i18next';

import { namespaces, TRANSLATION_FILES } from '../../constants/translation';
import config from '../../../config';
import '../../styles/components/crmContextBanner.less';

// The CRM context banner (MS-6626, T6.1).
//
// Shown for active CRM sessions and remembered booking context, and for persisted OutPerform
// bookings on direct visits. It renders
// a model built by src/utils/crmContext.js and decides nothing itself: which CRM, which lead,
// which buyer and whether the link was verified are all facts book-api reported, carried here
// unchanged. A null model renders nothing at all, so a page can mount the banner unconditionally.
//
// Never an identity number. The model has no field for one, and this component prints only the
// fields it names. That is the "no NRIC ever displayed from the link" acceptance criterion, held by
// construction rather than by a check.
//
// The verified tag is shown only when the model knows (a boolean). Null — a booking overview,
// where the session that knew has been consumed — omits the tag rather than print either word
// without evidence; "verified" against an unsigned link is the failure the tag exists to prevent.
const ns = { ns: TRANSLATION_FILES.translation };

export function CrmContextBanner({ context, recordURL, t }) {
  if (!context || !context.provider) return null;
  const {
    providerLabel, verified, leadRef, buyerName, buyerMobile, origin, fromLink,
  } = context;
  // The button leads to the booking's own record when the tenant's CRM can open one by URL
  // (recordURL, MS-6772), else to the static OutPerform entry page this build knows, else nowhere.
  const returnURL = origin === 'booking' && context.provider === 'outperform'
    ? (String(recordURL || '').trim() || config.get('outperformURL'))
    : '';
  const buyer = [buyerName, buyerMobile].filter(Boolean).join(' · ');
  const closeCopy = origin === 'booking' && !fromLink
    ? t('This booking is linked to {{crm}}.', { ...ns, crm: providerLabel })
    : origin === 'booking'
    ? t('This booking was opened from {{crm}}. Close this page when you are done.', { ...ns, crm: providerLabel })
    : t('This page was opened from {{crm}}. Close it when you are done.', { ...ns, crm: providerLabel });

  // The modifier says what the tag says: verified, unverified, or — when nothing is known —
  // neither, so an unknown state is not dressed in the unverified colour.
  let verification = '';
  if (verified === true) verification = ' crm-context-banner--verified';
  if (verified === false) verification = ' crm-context-banner--unverified';

  return (
    <div
      className={`crm-context-banner${verification}`}
      role="status"
      data-testid="crm-context-banner"
    >
      <div className="crm-context-banner__main"><div className="crm-context-banner__facts">
        <span className="crm-context-banner__provider" data-testid="crm-provider">
          <Icon name="linkify" />{providerLabel}
        </span>
        {leadRef ? (
          <span className="crm-context-banner__lead" data-testid="crm-lead-ref">
            {t('Ref', ns)} <code>{leadRef}</code>
          </span>
        ) : null}
        {buyer ? (
          <span className="crm-context-banner__buyer" data-testid="crm-buyer">{buyer}</span>
        ) : null}

      </div>
      <div className="crm-context-banner__close" data-testid="crm-close-copy">
        {returnURL && fromLink ? t('Continue in OutPerform.', ns) : closeCopy}
      </div></div>
      <div className="crm-context-banner__actions">
        {verified === true ? (
          <span className="crm-context-banner__tag crm-context-banner__tag--verified" data-testid="crm-verified">
            <Icon name="check circle" />{t('Verified link', ns)}
          </span>
        ) : null}
        {verified === false ? (
          <span className="crm-context-banner__tag crm-context-banner__tag--unverified" data-testid="crm-verified">
            <Icon name="warning circle" />{t('Unverified link', ns)}
          </span>
        ) : null}
        {returnURL && <Button as="a" role="link" href={returnURL} target="_blank" rel="noopener noreferrer" size="small" basic primary>
          {t('Open OutPerform', ns)} <Icon name="external alternate" />
        </Button>}
      </div>
    </div>
  );
}

CrmContextBanner.propTypes = {
  context: PropTypes.shape({
    provider: PropTypes.string,
    providerLabel: PropTypes.string,
    verified: PropTypes.oneOf([true, false, null]), // null: not known, no tag and no modifier
    leadRef: PropTypes.string,
    buyerName: PropTypes.string,
    buyerMobile: PropTypes.string,
    origin: PropTypes.oneOf(['session', 'booking']),
    fromLink: PropTypes.bool,
  }),
  recordURL: PropTypes.string,
  t: PropTypes.func.isRequired,
};

CrmContextBanner.defaultProps = {
  context: null,
  recordURL: '',
};

export default withTranslation(namespaces)(CrmContextBanner);



// WEBPACK FOOTER //
// ./src/components/Crm/CrmContextBanner.jsx